import { useEffect, useMemo, useState, useCallback } from "react";
import { createPriceMap, getVisibleMonthDates, getVisibleMonthDatesForMobile, getSingleMonthDate, getDateRangeFromVisibleMonthDates } from "../../utils/fareCalendar.js";
import { useFetchFareCalendar } from "./useFetchFareCalendar.js";
import { useActions, useSearchboxStore } from "../../store/searchBoxStore.js";
import { actions as searchActions } from "../../store/actions.js";
export const useFareCalendarEnabled = (segmentIndex, fareCalendarConfig, mode) => {
    const store = useSearchboxStore();
    const searchType = store.searchbox.type;
    const segment = store.searchbox.searchSegments?.[segmentIndex];
    const hasValidFrom = Boolean(segment?.from?.some((l) => l.type === "CITY" || l.type === "AIRPORT"));
    const hasValidTo = Boolean(segment?.to?.some((l) => l.type === "CITY" || l.type === "AIRPORT"));
    const isSupportedType = searchType === "ONEWAY" || searchType === "ROUNDTRIP";
    const isFareCalendarFeatureEnabled = mode === "vertical"
        ? fareCalendarConfig?.isFareCalendarMobileEnabled
        : fareCalendarConfig?.isFareCalendarDesktopEnabled;
    return isSupportedType && hasValidFrom && hasValidTo && Boolean(isFareCalendarFeatureEnabled);
};
export const useFareCalendar = ({ baseDate, mode, enabled = true, segmentIndex = 0, singleMonth = false, disableAutoFetch = false }) => {
    const [visibleMonths, setVisibleMonths] = useState([]);
    const actions = useActions(searchActions);
    const dateRange = useMemo(() => {
        if (visibleMonths.length === 0) {
            return undefined;
        }
        return getDateRangeFromVisibleMonthDates(visibleMonths);
    }, [visibleMonths]);
    const { data: flightsMap, loading: fetchLoading, error, legend } = useFetchFareCalendar({ dateRange, enabled, segmentIndex });
    const priceMap = useMemo(() => {
        return createPriceMap(flightsMap);
    }, [flightsMap]);
    const handleMonthChange = useCallback((date) => {
        if (singleMonth) {
            const months = getSingleMonthDate(date);
            setVisibleMonths(months);
        }
        else if (mode === "vertical") {
            const months = getVisibleMonthDatesForMobile(date);
            setVisibleMonths(months);
        }
        else {
            const months = getVisibleMonthDates(date);
            setVisibleMonths(months);
        }
    }, [mode, singleMonth]);
    useEffect(() => {
        if (!enabled || disableAutoFetch) {
            return;
        }
        const dateToUse = baseDate || new Date();
        if (singleMonth) {
            const months = getSingleMonthDate(dateToUse);
            setVisibleMonths(months);
        }
        else if (mode === "vertical") {
            const months = getVisibleMonthDatesForMobile(dateToUse);
            setVisibleMonths(months);
        }
        else {
            const months = getVisibleMonthDates(dateToUse);
            setVisibleMonths(months);
        }
    }, [baseDate, mode, enabled, singleMonth, disableAutoFetch]);
    useEffect(() => {
        if (!enabled) {
            actions.fareCalendarClear();
        }
    }, [enabled, actions]);
    useEffect(() => {
        return () => {
            actions.fareCalendarClear();
        };
    }, [actions]);
    const loading = enabled ? fetchLoading : false;
    return {
        priceMap,
        visibleMonths,
        handleMonthChange,
        loading,
        error,
        legend
    };
};
